'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Coffee, Check, X } from 'lucide-react'
import { useBriefPromoInitial } from '@/components/BriefPromoInitialProvider'
import { briefPromoAfterFetch, BRIEF_PROMO_DISMISS_KEY } from '@/lib/email/briefPromo'

/**
 * Prominent one-click opt-in to the daily "Morning Brief" email, shown on the flagship dashboard. Renders
 * ONLY when: the visitor is logged in, hasn't opted in yet, the brief can actually deliver (a real postal
 * address is configured server-side → `available`), and they haven't dismissed it. So it never invites an
 * opt-in the brief can't fulfill, and never nags a user who already turned it on or waved it off.
 * The rule itself (including why it defers to the money and verify asks) lives in src/lib/email/briefPromo.ts.
 *
 * 🚨 FIRST PAINT COMES FROM THE SERVER (register daily-brief-promo-pops-in-above-chart-1105). The root layout decides
 * the verdict and BriefPromoInitialProvider carries it here, so the card is in the server HTML or absent. It used to
 * start hidden and insert itself after three fetches, pushing the /chart grid down 128-191px on real loads. With no
 * server verdict (signed-in read failed or over budget) it still decides after mount, exactly as before.
 * A dismissed card, a share image and an embed are hidden before paint by the <head> script (briefPromoPrePaintScript)
 * through [data-brief-promo] in globals.css, since the server cannot see localStorage or the chart page's ?shot/?embed.
 */

// Dismissed or turned on during THIS document. The layout verdict is fixed at the first load and layouts persist
// across client navigations, so without this a soft navigation would repaint a card the visitor just answered.
let answeredThisDocument = false

export default function DailyBriefPromo() {
  const initial = useBriefPromoInitial()
  const onAlerts = usePathname() === '/alerts'
  const [show, setShow] = useState(() => !answeredThisDocument && !!initial && (onAlerts ? initial.alerts : initial.elsewhere))
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let dismissed = answeredThisDocument
    try { dismissed = dismissed || !!localStorage.getItem(BRIEF_PROMO_DISMISS_KEY) } catch { /* storage blocked */ }
    // Already hidden before paint by the <head> script, so unmounting it here moves nothing.
    if (dismissed) { setShow(false); return }
    let alive = true
    // ⚠ A FAILED money or verify check must not suppress the offer: each resolves to "silent" on error.
    const premiumKind = fetch('/api/user/premium-banner')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d && typeof d.kind === 'string' ? (d.kind as string) : null))
      .catch(() => null)
    const verifyAskActive = fetch('/api/auth/verification-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => !!d && d.needsVerification === true)
      .catch(() => false)

    // With a server verdict this is a revalidation: it agrees in steady state (same reads), and a failed daily-brief
    // read keeps what is painted. Without one it is the original after-mount decision.
    Promise.all([
      fetch('/api/email/daily-brief').then((r) => (r.ok ? r.json() : null)).catch(() => null),
      premiumKind,
      verifyAskActive,
    ])
      .then(([d, kind, verifying]) => {
        if (!alive) return
        setShow((cur) => briefPromoAfterFetch(cur, d, kind, verifying, onAlerts))
      })
      .catch(() => {})
    return () => { alive = false }
  }, [onAlerts])

  const enable = async () => {
    setBusy(true)
    try {
      const r = await fetch('/api/email/daily-brief', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: true }) })
      if (r.ok) { setDone(true); answeredThisDocument = true }
    } catch { /* leave visible to retry */ }
    setBusy(false)
  }

  const dismiss = () => { try { localStorage.setItem(BRIEF_PROMO_DISMISS_KEY, '1') } catch { /* ignore */ } answeredThisDocument = true; setShow(false) }

  if (!show) return null

  return (
    <div data-brief-promo="" className="relative mb-4 flex flex-col gap-3 overflow-hidden rounded-2xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-transparent dark:bg-gradient-to-br dark:from-amber-500/[0.10] dark:to-zinc-900/40 p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-5">
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full dark:bg-amber-500/10 blur-3xl" />
      <div className="relative z-10 flex min-w-0 flex-1 items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-500/30">
          <Coffee className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">Start your day ahead with the free Morning Brief</p>
          <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">
            {done
              ? "You're in. Look for it in your inbox each trading morning."
              : 'Today’s top play and why, the biggest gappers, who reports before the open, and your watchlist movers - emailed every trading morning.'}
          </p>
        </div>
      </div>
      {done ? (
        <span className="relative z-10 flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-[13px] font-bold text-emerald-700 dark:text-emerald-300">
          <Check className="h-4 w-4" /> Turned on
        </span>
      ) : (
        <div className="relative z-10 flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            className="rounded-lg bg-amber-500 px-4 py-2 text-[13px] font-bold text-zinc-950 transition-colors hover:bg-amber-400 disabled:opacity-60"
          >
            {busy ? 'Turning on…' : 'Get the free brief'}
          </button>
          <button type="button" onClick={dismiss} aria-label="Dismiss" title="No thanks" className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:text-foreground/80">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
